export const ENVIRONMENT_SETTINGS_PAGES = ['general', 'services', 'custom-domains'] as const

export type EnvironmentSettingsPage = (typeof ENVIRONMENT_SETTINGS_PAGES)[number]

export const ENVIRONMENT_SETTINGS_PAGE_SEGMENTS: Record<EnvironmentSettingsPage, string> = {
  general: 'general-settings',
  services: 'services',
  'custom-domains': 'custom-domains',
}

export const ENVIRONMENT_SETTINGS_PAGE_ROUTE_SEGMENTS = ENVIRONMENT_SETTINGS_PAGES.map(
  (page) => ENVIRONMENT_SETTINGS_PAGE_SEGMENTS[page],
)

export const ENVIRONMENT_SETTINGS_PAGE_TITLES: Record<EnvironmentSettingsPage, string> = {
  general: 'General',
  services: 'Services',
  'custom-domains': 'Custom domains',
}

export const ENVIRONMENT_SETTINGS_NAV_LABELS: Record<EnvironmentSettingsPage, string> = {
  general: 'General',
  services: 'Services',
  'custom-domains': 'Custom Domains',
}

export type EnvironmentSettingsNavItem = {
  page: EnvironmentSettingsPage
  label: string
  path: string
}

export function environmentDetailPath(projectId: string | number, envId: string | number): string {
  return `/project/${projectId}/environment/${envId}`
}

export function environmentSettingsPagePath(
  projectId: string | number,
  envId: string | number,
  page: EnvironmentSettingsPage,
): string {
  return `${environmentDetailPath(projectId, envId)}/${ENVIRONMENT_SETTINGS_PAGE_SEGMENTS[page]}`
}

export function environmentServiceSettingsPath(
  projectId: string | number,
  envId: string | number,
  mappingId: string | number,
): string {
  return `${environmentDetailPath(projectId, envId)}/service/${mappingId}/settings`
}

export function environmentSettingsPathSegment(
  pathname: string,
  projectId: string,
  envId: string,
): string | null {
  const prefix = `${environmentDetailPath(projectId, envId)}/`
  if (!pathname.startsWith(prefix)) return null
  const rest = pathname.slice(prefix.length).replace(/\/$/, '')
  if (!rest || rest.includes('/')) return null
  if (!(ENVIRONMENT_SETTINGS_PAGE_ROUTE_SEGMENTS as readonly string[]).includes(rest)) return null
  return rest
}

export function parseEnvironmentSettingsPageSegment(
  segment: string | null | undefined,
): EnvironmentSettingsPage {
  if (!segment) return 'general'
  const entry = Object.entries(ENVIRONMENT_SETTINGS_PAGE_SEGMENTS).find(([, path]) => path === segment)
  if (entry) return entry[0] as EnvironmentSettingsPage
  return 'general'
}

/** Tab ids from the old `/environment/:id/settings?tab=` hub. */
export function parseEnvironmentSettingsTabParam(raw: string | null | undefined): EnvironmentSettingsPage {
  const t = (raw ?? 'general').trim().toLowerCase()
  if (t === 'services' || t === 'custom-domains') return t
  return 'general'
}

export function isLegacyEnvironmentSettingsHubPath(
  pathname: string,
  projectId: string,
  envId: string,
): boolean {
  const base = `${environmentDetailPath(projectId, envId)}/settings`
  return pathname.replace(/\/$/, '') === base
}

export function isEnvironmentServiceSettingsPath(
  pathname: string,
  projectId: string,
  envId: string,
): boolean {
  const prefix = `${environmentDetailPath(projectId, envId)}/service/`
  if (!pathname.startsWith(prefix)) return false
  const rest = pathname.slice(prefix.length).replace(/\/$/, '')
  const parts = rest.split('/')
  return parts.length === 2 && parts[0] !== '' && parts[1] === 'settings'
}

export function isEnvironmentSettingsPath(pathname: string, projectId: string, envId: string): boolean {
  return environmentSettingsPathSegment(pathname, projectId, envId) != null
}

export function isEnvironmentOverviewPath(
  pathname: string,
  projectId: string,
  envId: string,
): boolean {
  return pathname.replace(/\/$/, '') === environmentDetailPath(projectId, envId)
}

export function isEnvironmentSidebarPath(pathname: string, projectId: string, envId: string): boolean {
  const base = environmentDetailPath(projectId, envId)
  const normalized = pathname.replace(/\/$/, '')
  return normalized === base || normalized.startsWith(`${base}/`)
}

/** Back target for the environment sidebar: always the owning project. */
export function environmentSidebarExitPath(projectId: string): string {
  return `/project/${projectId}`
}

/** Sidebar target for `/deployment/:id` once the deployment's project and environment are known. */
export function environmentContextForDeployment(
  projectId: string | null,
  envId: string | null,
): { projectId: string; envId: string; exitPath: string } | null {
  if (!projectId || !envId) return null
  return {
    projectId,
    envId,
    exitPath: environmentDetailPath(projectId, envId),
  }
}

export function getEnvironmentSettingsNavItems(
  projectId: string,
  envId: string,
): EnvironmentSettingsNavItem[] {
  return ENVIRONMENT_SETTINGS_PAGES.map((page) => ({
    page,
    label: ENVIRONMENT_SETTINGS_NAV_LABELS[page],
    path: environmentSettingsPagePath(projectId, envId, page),
  }))
}

export function isEnvironmentSettingsNavItemActive(
  pathname: string,
  projectId: string,
  envId: string,
  page: EnvironmentSettingsPage,
): boolean {
  const current = pathname.replace(/\/$/, '')
  const target = environmentSettingsPagePath(projectId, envId, page).replace(/\/$/, '')
  if (current === target) return true
  return page === 'services' && isEnvironmentServiceSettingsPath(pathname, projectId, envId)
}

export function environmentSettingsLegacySearchRedirect(
  projectId: string,
  envId: string,
  search: string,
): string {
  const params = new URLSearchParams(search)
  const page = parseEnvironmentSettingsTabParam(params.get('tab'))
  params.delete('tab')
  const qs = params.toString()
  const path = environmentSettingsPagePath(projectId, envId, page)
  return qs ? `${path}?${qs}` : path
}
